/**
 * Iter-59 — Help Center landing + article shell.
 *
 * Serves 3 modes:
 *   1. Landing: role cards + search + category grid (default `/help`).
 *   2. Category browse: `/help?category=focused_funnel` narrows the grid.
 *   3. Article detail: `/help?slug=<slug>` renders the full article.
 *
 * Server-side RBAC on `/api/help/*` guarantees that a caller can only
 * see + open articles their role permits, so this component doesn't
 * need to re-check permissions.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BookOpen, MagnifyingGlass, ArrowLeft, Sparkle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { HelpArticleRenderer } from "./HelpArticleRenderer";
import { HelpRoleBadge } from "./HelpPrimitives";
import { GuideScreenshotsPanel } from "./GuideScreenshotsPanel";
import { DownloadPdfButton } from "./DownloadPdfButton";

const ROLE_LABEL = {
  admin: "Admin Guide",
  employee: "Employee Guide",
  media_buyer: "Media Buyer Guide",
  vendor: "Affiliate Guide",
  buyer: "Buyer Guide",
  shared: "General",
};

const ROLE_BLURB = {
  admin: "Run the whole platform: clients, affiliates, pages, routing, delivery, billing and settings.",
  employee: "Day-to-day work: leads, returns, landing pages, buyers and delivery.",
  media_buyer: "Landing pages, pixels, custom tracking, attribution and traffic results.",
  vendor: "Your portal, page links, rotating links, marketplace earnings and leads.",
  buyer: "Your portal, delivered leads, page profile, testimonials and design tests.",
  shared: "Basics everyone should know.",
};

const CATEGORY_LABEL = {
  getting_started: "Getting Started",
  leads: "Leads",
  landing_pages: "Landing Pages",
  focused_funnel: "Focused Funnel",
  quiz: "Quiz Forms",
  ab_testing: "A/B Testing",
  buyers: "Buyers / Clients",
  vendors: "Affiliates",
  media_buyers: "Media Buyers",
  routing: "Routing",
  delivery: "Delivery",
  integrations: "Integrations",
  pixels: "Tracking / Pixels",
  custom_tracking: "Custom Tracking",
  compliance: "Compliance",
  trustedform: "TrustedForm",
  reporting: "Reporting / Analytics",
  returns: "Returns",
  billing: "Billing / Invoices",
  employees: "Employees & Permissions",
  preview_demo: "Preview / Demo Tools",
  settings: "Settings",
  troubleshooting: "Troubleshooting",
  security: "Security",
};

export default function HelpCenter() {
  const [params, setParams] = useSearchParams();
  const slug = params.get("slug");
  const category = params.get("category") || "";
  const asRoleParam = params.get("as_role") || "";

  const [rolesInfo, setRolesInfo] = useState(null);
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  // Fetch roles once. Admins get can_view_as=true.
  useEffect(() => {
    api.get("/help/roles")
      .then((r) => setRolesInfo(r.data))
      .catch(() => setRolesInfo({ roles: [], can_view_as: false, current: null }));
  }, []);

  // Fetch either the article detail or the list.
  useEffect(() => {
    setLoading(true);
    if (slug) {
      const q = asRoleParam ? `?as_role=${encodeURIComponent(asRoleParam)}` : "";
      api.get(`/help/articles/${slug}${q}`)
        .then((r) => setArticle(r.data))
        .catch(() => setArticle(null))
        .finally(() => setLoading(false));
    } else {
      const p = new URLSearchParams();
      if (asRoleParam) p.set("as_role", asRoleParam);
      if (category) p.set("category", category);
      const qs = p.toString() ? `?${p.toString()}` : "";
      api.get(`/help/articles${qs}`)
        .then((r) => setArticles(r.data.items || []))
        .catch(() => setArticles([]))
        .finally(() => setLoading(false));
    }
  }, [slug, category, asRoleParam]);

  // Search — debounced. Server enforces role visibility.
  const runSearch = useCallback((qStr) => {
    if (!qStr) { setSearchResults(null); return; }
    const p = new URLSearchParams();
    p.set("q", qStr);
    if (asRoleParam) p.set("as_role", asRoleParam);
    api.get(`/help/search?${p.toString()}`)
      .then((r) => setSearchResults(r.data.items || []))
      .catch(() => setSearchResults([]));
  }, [asRoleParam]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const groupedByRole = useMemo(() => {
    const out = {};
    for (const a of articles) {
      const roles = a.role_visibility || ["shared"];
      const primary = ["admin", "employee", "media_buyer", "vendor", "buyer", "shared"]
        .find((r) => roles.includes(r)) || "shared";
      (out[primary] = out[primary] || []).push(a);
    }
    return out;
  }, [articles]);

  const groupedByCategory = useMemo(() => {
    const out = {};
    for (const a of articles) {
      const c = a.category || "general";
      (out[c] = out[c] || []).push(a);
    }
    return out;
  }, [articles]);

  const setAsRole = (r) => {
    const next = new URLSearchParams(params);
    if (r) next.set("as_role", r); else next.delete("as_role");
    next.delete("slug");
    setParams(next);
  };

  // === ARTICLE DETAIL ===
  if (slug) {
    return (
      <div className="max-w-4xl" data-testid="help-center-detail">
        <button
          onClick={() => { const n = new URLSearchParams(params); n.delete("slug"); setParams(n); }}
          data-testid="help-back-button"
          className="text-[11px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white mb-4 flex items-center gap-1"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Help Center
        </button>
        {loading ? (
          <div className="brut-card p-8 text-center label-mono" data-testid="help-loading">Loading…</div>
        ) : !article ? (
          <div className="brut-card p-8 text-center" data-testid="help-not-found">
            <div className="font-chivo font-black text-2xl uppercase tracking-tighter">Article not found.</div>
            <p className="mt-2 text-sm text-zinc-600">It may have moved, or your role doesn't have access.</p>
          </div>
        ) : (
          <article className="brut-card p-8" data-testid="help-article">
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              {(article.role_visibility || []).map((r) => <HelpRoleBadge key={r} role={r} />)}
              <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-100 border border-zinc-300 px-2 py-0.5">
                {CATEGORY_LABEL[article.category] || article.category}
              </span>
            </div>
            <h1 className="font-chivo font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none" data-testid="help-article-title">
              {article.title}
            </h1>
            <p className="mt-3 text-sm text-zinc-700 leading-relaxed" data-testid="help-article-summary">
              {article.summary}
            </p>
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid="help-article-updated">
              Last updated {article.updated_at} · {article.version}
            </div>
            {article.pdf && <div className="mt-3"><DownloadPdfButton slug={article.slug} /></div>}
            <hr className="border-t-2 border-black my-6" />
            <HelpArticleRenderer sections={article.sections || []} />
            {(article.related && article.related.length > 0) && (
              <div className="mt-8 pt-4 border-t border-black">
                <div className="label-mono mb-2">Related</div>
                <ul className="space-y-1">
                  {(article.related_items || article.related.map((s) => ({ slug: s, title: s }))).map(({ slug: s, title }) => (
                    <li key={s}>
                      <button
                        onClick={() => { const n = new URLSearchParams(params); n.set("slug", s); setParams(n); }}
                        className="text-sm underline hover:bg-yellow-300"
                        data-testid={`help-related-${s}`}
                      >
                        {title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        )}
      </div>
    );
  }

  // === LANDING ===
  const showAsRole = rolesInfo?.can_view_as;
  const currentAsRole = asRoleParam || null;

  return (
    <div className="max-w-6xl" data-testid="help-center">
      <div className="mb-6">
        <div className="label-mono">Help</div>
        <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none flex items-center gap-3">
          <BookOpen size={40} weight="bold" /> Help Center
        </h1>
        <div className="mt-1 text-sm text-zinc-600">
          Guides, walkthroughs, and troubleshooting for every LeadFlow feature you can use.
        </div>
      </div>

      <GuideScreenshotsPanel />

      {/* Search + admin view-as-role */}
      <div className="brut-card p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 border border-black px-2 py-1">
          <MagnifyingGlass size={16} weight="bold" />
          <input
            data-testid="help-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guides, features, or questions…"
            className="flex-1 bg-transparent outline-none text-sm py-1"
          />
        </div>
        {showAsRole && (
          <div className="flex items-center gap-2" data-testid="help-view-as-controls">
            <span className="label-mono">View as</span>
            <select
              data-testid="help-view-as-select"
              value={currentAsRole || ""}
              onChange={(e) => setAsRole(e.target.value)}
              className="border border-black px-2 py-1 text-xs uppercase tracking-widest bg-white"
            >
              <option value="">All (admin)</option>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
              <option value="media_buyer">Media Buyer</option>
              <option value="vendor">Affiliate</option>
              <option value="buyer">Buyer</option>
            </select>
          </div>
        )}
      </div>

      {/* Search results override the normal listing when present */}
      {searchResults !== null ? (
        <div className="brut-card p-4" data-testid="help-search-results">
          <div className="label-mono mb-3">Search results for “{query}” — {searchResults.length}</div>
          {searchResults.length === 0 ? (
            <div className="text-sm text-zinc-600">No matching guides. Try a shorter query.</div>
          ) : (
            <ul className="space-y-2">
              {searchResults.map((r) => (
                <li key={r.slug}>
                  <button
                    onClick={() => { const n = new URLSearchParams(params); n.set("slug", r.slug); setParams(n); }}
                    className="w-full text-left border border-black bg-white p-3 hover:bg-yellow-50"
                    data-testid={`help-search-hit-${r.slug}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {(r.role_visibility || []).map((role) => <HelpRoleBadge key={role} role={role} />)}
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        {CATEGORY_LABEL[r.category] || r.category}
                      </span>
                    </div>
                    <div className="font-bold text-sm">{r.title}</div>
                    <div className="text-xs text-zinc-600 mt-1">{r.summary}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : loading ? (
        <div className="brut-card p-8 text-center label-mono" data-testid="help-loading">Loading…</div>
      ) : (
        <>
          {/* Role cards for admins */}
          {showAsRole && !currentAsRole && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="help-role-cards">
              {["admin", "employee", "media_buyer", "vendor", "buyer"].map((r) => (
                <button
                  key={r}
                  onClick={() => setAsRole(r)}
                  data-testid={`help-role-card-${r}`}
                  className="border border-black bg-white p-4 text-left hover:bg-yellow-50 hover:-translate-y-0.5 transition-transform"
                >
                  <HelpRoleBadge role={r} />
                  <div className="font-chivo font-black text-lg mt-2 uppercase tracking-tighter">{ROLE_LABEL[r]}</div>
                  <div className="text-xs text-zinc-600 mt-1">{ROLE_BLURB[r]}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest mt-2 text-zinc-500">
                    {(groupedByRole[r] || []).length} articles
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Category-grouped article list */}
          <div className="brut-card p-4" data-testid="help-catalog">
            {currentAsRole && (
              <div className="mb-3 flex items-center gap-2">
                <span className="label-mono">Viewing as</span>
                <HelpRoleBadge role={currentAsRole} />
                <button
                  onClick={() => setAsRole("")}
                  className="text-[10px] uppercase tracking-widest underline hover:bg-yellow-300"
                  data-testid="help-clear-as-role"
                >
                  clear
                </button>
              </div>
            )}
            {Object.keys(groupedByCategory).length === 0 ? (
              <div className="text-center py-8 text-sm text-zinc-600 flex flex-col items-center gap-2">
                <Sparkle size={24} weight="bold" />
                No articles available for your role yet.
              </div>
            ) : (
              Object.entries(groupedByCategory)
                .sort(([a], [b]) => (a === "getting_started" ? -1 : b === "getting_started" ? 1 : (CATEGORY_LABEL[a] || a).localeCompare(CATEGORY_LABEL[b] || b)))
                .map(([cat, items]) => (
                  <div key={cat} className="mb-6 last:mb-0">
                    <h2 className="font-chivo font-black text-xl uppercase tracking-tighter mb-2 pb-1 border-b border-black">
                      {CATEGORY_LABEL[cat] || cat}
                    </h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {items.map((a) => (
                        <li key={a.slug}>
                          <button
                            onClick={() => { const n = new URLSearchParams(params); n.set("slug", a.slug); setParams(n); }}
                            data-testid={`help-article-card-${a.slug}`}
                            className="w-full text-left border border-black bg-white p-3 hover:bg-yellow-50"
                          >
                            <div className="font-bold text-sm">{a.title}</div>
                            <div className="text-xs text-zinc-600 mt-1">{a.summary}</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                              {a.updated_at}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
